/**
 * auth-api.ts — Gọi API công khai đăng ký / Google / quên mật khẩu (backend: auth/signup/signup-routes.ts).
 */
import { useEffect, useState } from 'react'
import type { AxiosError } from 'axios'
import { api } from '@/lib/api-client'
import { apiErrorText } from '@/lib/api-error-text'
import type { AuthUser } from '@/types/api'
import { withTurnstileToken } from '@/lib/turnstile'
import { i18n } from '@/lib/i18n'

export interface SignupConfig {
  enabled: boolean
  googleClientId: string | null
  /** Khoá công khai Cloudflare Turnstile; null = chưa bật (không vẽ widget, không gửi token). Dùng chung cho mọi form công khai. */
  turnstileSiteKey: string | null
  termsUrl: string | null
  privacyUrl: string | null
  supportContact: string | null
  otpTtlSec: number
  resendCooldownSec: number
}

export interface SessionResponse {
  token: string
  refreshToken?: string
  user: AuthUser
}

export interface OtpSent {
  ok: true
  email: string
  expiresInSec: number
  resendInSec: number
  devMode?: boolean
}

export type GoogleResult =
  | ({ status: 'logged_in' } & SessionResponse)
  /** Tài khoản bật / bắt buộc xác thực 2 lớp: chưa có phiên, chỉ có vé tạm để nhập / thiết lập mã. */
  | { status: 'totp_required'; mode: 'verify' | 'setup'; ticket: string }
  | { status: 'needs_completion'; signupToken: string; profile: { email: string; fullName: string } }

export interface ApiErr {
  message: string
  code?: string
  field?: string
  fields?: Record<string, string>
  retryAfter?: number
  status?: number
}

/** Lỗi API → thông điệp + mã + trường (để tô đỏ ô nhập). */
export function toApiErr(err: unknown): ApiErr {
  const e = err as AxiosError<{ error?: string; message?: string; code?: string; field?: string; fields?: Record<string, string>; retryAfter?: number }>
  const d = e?.response?.data
  const status = e?.response?.status
  if (!e?.response) return { message: i18n.t('authPages:apiErrors.noConnection') }
  return {
    // Lỗi mặc định của Fastify: `error` chỉ là tên mã HTTP ("Unauthorized"), câu tiếng Việt nằm ở `message`.
    message: apiErrorText(d) || (status === 429 ? i18n.t('authPages:apiErrors.tooManyRequests') : i18n.t('authPages:apiErrors.generic')),
    code: d?.code,
    field: d?.field,
    fields: d?.fields,
    retryAfter: d?.retryAfter,
    status,
  }
}

/** Thông điệp lỗi đăng nhập tiếng Việt (429 = tạm khoá do thử quá nhiều lần). */
export function loginErrorMessage(err: unknown): string {
  const a = toApiErr(err)
  if (a.status === 429) return i18n.t('auth:login.tooManyAttempts')
  return a.message
}

/** Vé "Hoàn tất đăng ký Google" chuyển từ trang Đăng nhập sang Đăng ký (location.state). */
export function googleSignupFromState(state: unknown): { signupToken: string; email: string; fullName: string } | null {
  const g = (state as { googleSignup?: { signupToken?: unknown; email?: unknown; fullName?: unknown } } | null)?.googleSignup
  if (!g || typeof g.signupToken !== 'string' || typeof g.email !== 'string') return null
  return { signupToken: g.signupToken, email: g.email, fullName: typeof g.fullName === 'string' ? g.fullName : '' }
}

export const authApi = {
  config: () => api.get<SignupConfig>('/auth/signup/config').then((r) => r.data),
  registerStart: (body: Record<string, unknown>) => api.post<OtpSent>('/auth/register/start', body).then((r) => r.data),
  registerResend: (email: string) => api.post<OtpSent>('/auth/register/resend', { email }).then((r) => r.data),
  registerVerify: (email: string, code: string) => api.post<SessionResponse>('/auth/register/verify', { email, code }).then((r) => r.data),
  google: (credential: string) => api.post<GoogleResult>('/auth/google', { credential }).then((r) => r.data),
  googleComplete: (body: Record<string, unknown>) => api.post<SessionResponse>('/auth/google/complete', body).then((r) => r.data),
  forgot: (email: string, turnstileToken?: string | null) =>
    api.post<{ ok: true; message: string; resendInSec: number }>('/auth/password/forgot', withTurnstileToken({ email }, turnstileToken)).then((r) => r.data),
  reset: (email: string, code: string, newPassword: string) =>
    api.post<{ ok: true; message: string }>('/auth/password/reset', { email, code, newPassword }).then((r) => r.data),
}

let configPromise: Promise<SignupConfig | null> | null = null
/** Đã tải xong (kể cả null) → dùng ngay, không nháy "Đang tải" khi đổi Đăng nhập ↔ Đăng ký trong hộp. */
let configCache: { value: SignupConfig | null } | null = null

/** Cấu hình đăng ký công khai (đệm trong phiên trang). null = không tải được (vẫn cho dùng form). */
export function useSignupConfig(): { config: SignupConfig | null; loading: boolean } {
  const [state, setState] = useState<{ config: SignupConfig | null; loading: boolean }>(() =>
    configCache ? { config: configCache.value, loading: false } : { config: null, loading: true })
  useEffect(() => {
    let alive = true
    if (!configPromise) {
      configPromise = authApi.config().catch(() => {
        configPromise = null
        return null
      })
    }
    void configPromise.then((c) => {
      if (c) configCache = { value: c }
      if (alive) setState({ config: c, loading: false })
    })
    return () => {
      alive = false
    }
  }, [])
  return state
}

/** Client ID Google: biến build VITE_GOOGLE_CLIENT_ID, không có thì lấy từ backend (GOOGLE_CLIENT_ID). */
export function resolveGoogleClientId(config: SignupConfig | null): string | null {
  const fromEnv = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim()
  return fromEnv || config?.googleClientId || null
}
